import { KeywordButtonType } from "@components/Button/utils/types";
import KeywordFilter from "@components/KeywordFilter";
import { KeywordType } from "@components/KeywordFilter/utils/types";
import { BUTTON_CSS } from "@features/toolbar/ArticleToolbar/utils/constants";
import { FeedTag } from "@pages/Keyword/utils/types";
import type { Tag } from "@typings/Cue";
import { MYBT_EXCLUDE_KEYWORDS } from "@util/constant";
import { cn } from "@util/helpers";

export enum KeywordDisplayOption {
  First = "first",
  Rest = "rest",
  ToolBar = "toolbar",
}

export type ArticleKeywordsProps = {
  keywords: FeedTag[];
  keywordDisplayOption?: KeywordDisplayOption;
  keywordButtonType?: KeywordButtonType;
  rootClassName?: string;
  tagClassName?: string;
  firstKeyworkDisplayClassname?: string;
  toolTipClassName?: string;
  toolTipArrowClassName?: string;
  disableTooltip?: boolean;
  withGiftIcon?: boolean;
  disableBorder?: boolean;
};

export default function ArticleKeywords({
  keywords,
  keywordDisplayOption,
  firstKeyworkDisplayClassname,
  rootClassName,
  tagClassName,
  toolTipClassName,
  toolTipArrowClassName,
  disableTooltip,
  disableBorder,
  keywordButtonType,
}: ArticleKeywordsProps): React.ReactElement {
  const tags = keywords
    .filter((tag) => {
      return !MYBT_EXCLUDE_KEYWORDS.some((t) => t === tag.name);
    })
    .map((tag) => {
      const id = tag.id;
      // Provide default values for missing Tag properties
      return {
        ...tag,
        id: id.toString(),
        uri: "",
        urlPathHistory: [],
        aliases: [],
        fields: [],
        parent: null,
      };
    }) as Tag[]; // TODO: Remove this when the other files are updated to use the FeedTag type

  switch (keywordDisplayOption) {
    case KeywordDisplayOption.First:
      return (
        <div
          data-testid="article-keyword-filter-first"
          className={firstKeyworkDisplayClassname}
        >
          {tags && tags[0] ? (
            <KeywordFilter
              testId={"article-top-keyword"}
              tag={tags[0]}
              rootClassName={cn("bg-gray-250 p-3 inline-block", rootClassName)}
              tagClassName={cn("!text-gray-850", tagClassName)}
              toolTipClassName={toolTipClassName}
              toolTipArrowClassName={toolTipArrowClassName}
              disableTooltip={disableTooltip}
              disableBorder={disableBorder}
              keywordButtonType={keywordButtonType}
            />
          ) : null}
        </div>
      );
    case KeywordDisplayOption.Rest:
      return (
        <div data-testid="article-keyword-filter-rest">
          {tags && tags.length > 1 && (
            <div className="mb-6 mt-3 border-t border-gray-75 pt-3">
              <h2 className="mb-3 font-poppins text-lg font-semibold">
                KEYWORDS IN THIS ARTICLE
              </h2>

              <div className="flex flex-wrap gap-3">
                {/* Skip the first keyword as it has already been displayed below the author details */}
                {tags.slice(1).map((keyword) => (
                  <KeywordFilter
                    key={keyword.uri}
                    tag={keyword}
                    rootClassName="bg-gray-250 p-3 inline-block"
                    tagClassName="!text-gray-850"
                    disableTooltip
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      );

    case KeywordDisplayOption.ToolBar:
      return (
        <KeywordFilter
          testId="keyword-button-component"
          tag={tags[0]}
          keywordButtonType={KeywordButtonType.version3}
          keywordType={KeywordType.noTag}
          disableTooltip
          buttonClassName={BUTTON_CSS}
        />
      );
    default:
      return <></>;
  }
}
